import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ApiResponse } from '@classes/api-response';
import { ConfigurableListOption, MaterialOption, Settings } from '@models/settings';
import {
  DEFAULT_AWAITING_RX_FOLLOW_UP_HOURS,
  DEFAULT_INQUIRY_FOLLOW_UP_HOURS,
  DEFAULT_REPAIR_LOCATIONS,
  DEFAULT_WHO_TO_CALL,
  IN_HOUSE_REPAIR_LOCATION,
  activeListLabels,
  normalizeConfigurableList,
} from '../constants/task-lists';

export interface PricingSettings {
  dutyAmount: number;
  nosepadsInstallAmount: number;
  drillMountAmount: number;
  restockingFeePercent: number;
  salesTaxRate: number;
  materials: MaterialOption[];
  repairLocations?: ConfigurableListOption[];
  whoToCallOptions?: ConfigurableListOption[];
  inquiryFollowUpHours?: number;
  awaitingRxFollowUpHours?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private baseUrl = `${environment.base_url}/settings`;
  dutyAmount = 50;
  nosepadsInstallAmount = 0;
  drillMountAmount = 0;
  restockingFeePercent = 15;
  salesTaxRate = 0;
  materials: MaterialOption[] = [];
  repairLocations: ConfigurableListOption[] = DEFAULT_REPAIR_LOCATIONS.map((item) => ({
    ...item,
  }));
  whoToCallOptions: ConfigurableListOption[] = DEFAULT_WHO_TO_CALL.map((item) => ({
    ...item,
  }));
  inquiryFollowUpHours = DEFAULT_INQUIRY_FOLLOW_UP_HOURS;
  awaitingRxFollowUpHours = DEFAULT_AWAITING_RX_FOLLOW_UP_HOURS;

  constructor(private http: HttpClient) {}

  loadSettings(): Observable<Settings> {
    return this.http.get<ApiResponse<Settings>>(this.baseUrl).pipe(
      map((res) => new Settings(res.settings)),
      tap((settings) => this.applySettings(settings))
    );
  }

  updateDutyAmount(dutyAmount: number): Observable<Settings> {
    return this.updatePricingSettings({
      dutyAmount,
      nosepadsInstallAmount: this.nosepadsInstallAmount,
      drillMountAmount: this.drillMountAmount,
      restockingFeePercent: this.restockingFeePercent,
      salesTaxRate: this.salesTaxRate,
      materials: this.materials,
      repairLocations: this.repairLocations,
      whoToCallOptions: this.whoToCallOptions,
      inquiryFollowUpHours: this.inquiryFollowUpHours,
      awaitingRxFollowUpHours: this.awaitingRxFollowUpHours,
    });
  }

  updatePricingSettings(pricing: PricingSettings): Observable<Settings> {
    const materials = Array.isArray(pricing.materials)
      ? pricing.materials.map((item) => ({
          label: String(item?.label ?? '').trim(),
          price: Math.round(parseFloat(String(item?.price ?? 0)) * 100) / 100,
        }))
      : [];
    const repairLocations = this.normalizeList(pricing.repairLocations, this.repairLocations);
    const whoToCallOptions = this.normalizeList(
      pricing.whoToCallOptions,
      this.whoToCallOptions
    );
    const body = {
      dutyAmount: pricing.dutyAmount,
      nosepadsInstallAmount: pricing.nosepadsInstallAmount,
      drillMountAmount: pricing.drillMountAmount,
      restockingFeePercent: pricing.restockingFeePercent,
      salesTaxRate: pricing.salesTaxRate,
      materials,
      repairLocations,
      whoToCallOptions,
      inquiryFollowUpHours:
        pricing.inquiryFollowUpHours ?? this.inquiryFollowUpHours,
      awaitingRxFollowUpHours:
        pricing.awaitingRxFollowUpHours ?? this.awaitingRxFollowUpHours,
    };
    return this.http.put<ApiResponse<Settings>>(`${this.baseUrl}/pricing`, body).pipe(
      map((res) => {
        const settings = new Settings(res.settings);
        if (!Array.isArray(res?.settings?.materials)) {
          settings.materials = materials.map((item) => new MaterialOption(item));
        }
        if (!Array.isArray(res?.settings?.repairLocations)) {
          settings.repairLocations = repairLocations.map(
            (item) => new ConfigurableListOption(item)
          );
        }
        if (!Array.isArray(res?.settings?.whoToCallOptions)) {
          settings.whoToCallOptions = whoToCallOptions.map(
            (item) => new ConfigurableListOption(item)
          );
        }
        return settings;
      }),
      tap((settings) => this.applySettings(settings))
    );
  }

  getRetailPlusDuty(retail: number | string): number {
    let value = typeof retail === 'string' ? parseFloat(retail.replace(/,/g, '')) : retail;
    value = isNaN(value) ? 0 : value;
    return value + this.dutyAmount;
  }

  getCustomAddOnTotal(options: {
    duty?: boolean;
    nosepadInstall?: boolean;
    drill?: boolean;
    materialPrice?: number | string | null;
  }): number {
    let total = 0;
    if (options.duty) {
      total += this.dutyAmount;
    }
    if (options.nosepadInstall) {
      total += this.nosepadsInstallAmount;
    }
    if (options.drill) {
      total += this.drillMountAmount;
    }
    if (options.materialPrice != null && options.materialPrice !== '') {
      const materialPrice =
        typeof options.materialPrice === 'string'
          ? parseFloat(options.materialPrice.replace(/,/g, ''))
          : options.materialPrice;
      if (!isNaN(materialPrice) && materialPrice > 0) {
        total += materialPrice;
      }
    }
    return total;
  }

  getMaterialPrice(label: string): number {
    const match = this.materials.find((item) => item.label === label);
    return match ? match.price : 0;
  }

  activeRepairLocations(current?: string): string[] {
    const labels = activeListLabels(this.repairLocations, DEFAULT_REPAIR_LOCATIONS);
    if (!labels.includes(IN_HOUSE_REPAIR_LOCATION)) {
      labels.unshift(IN_HOUSE_REPAIR_LOCATION);
    }
    const value = String(current || '').trim();
    if (value && !labels.includes(value)) {
      labels.push(value);
    }
    return labels;
  }

  activeWhoToCallOptions(current?: string): string[] {
    const labels = activeListLabels(this.whoToCallOptions, DEFAULT_WHO_TO_CALL);
    const value = String(current || '').trim();
    if (value && !labels.includes(value) && value !== 'Other') {
      labels.push(value);
    }
    return labels;
  }

  private normalizeList(
    options: ConfigurableListOption[] | undefined,
    fallback: ConfigurableListOption[]
  ): ConfigurableListOption[] {
    return normalizeConfigurableList(options, fallback);
  }

  private applySettings(settings: Settings): void {
    this.dutyAmount = settings.dutyAmount;
    this.nosepadsInstallAmount = settings.nosepadsInstallAmount;
    this.drillMountAmount = settings.drillMountAmount;
    this.restockingFeePercent =
      settings.restockingFeePercent == null || isNaN(settings.restockingFeePercent)
        ? 15
        : settings.restockingFeePercent;
    this.salesTaxRate =
      settings.salesTaxRate == null || isNaN(settings.salesTaxRate)
        ? 0
        : settings.salesTaxRate;
    this.materials = Array.isArray(settings.materials) ? settings.materials : [];
    this.repairLocations = normalizeConfigurableList(
      settings.repairLocations,
      DEFAULT_REPAIR_LOCATIONS
    );
    this.whoToCallOptions = normalizeConfigurableList(
      settings.whoToCallOptions,
      DEFAULT_WHO_TO_CALL
    );
    this.inquiryFollowUpHours =
      settings.inquiryFollowUpHours > 0
        ? settings.inquiryFollowUpHours
        : DEFAULT_INQUIRY_FOLLOW_UP_HOURS;
    this.awaitingRxFollowUpHours =
      settings.awaitingRxFollowUpHours > 0
        ? settings.awaitingRxFollowUpHours
        : DEFAULT_AWAITING_RX_FOLLOW_UP_HOURS;
  }
}
